import {
  ArrowsRightLeftIcon,
  BriefcaseIcon,
  CloudIcon,
  CodeBracketIcon,
  CpuChipIcon,
  PhoneIcon,
  ServerStackIcon,
} from '@heroicons/react/24/outline';

/**
 * Visual identity per service category, for the catalogue cards.
 *
 * Why this exists: with thirty-eight cards whose only differentiator is words,
 * the eye has nothing to sort by and the reader ends up reading every card to
 * find the one relevant to them. Encoding the category as a colour and an icon
 * lets someone scanning for "do they do cloud work" find it without reading.
 *
 * Two accessibility rules are baked in rather than left to the caller:
 *
 * 1. Colour is REDUNDANT, never the sole carrier of meaning (WCAG 1.4.1) — the
 *    category is always written in text on the same card.
 * 2. `accent` and `ink` are separate because Beach (#8FD7E5) is 1.6:1 on white
 *    and is a decorative colour only. Anything that must be legible uses `ink`,
 *    which is ≥3:1 on white for every category (WCAG 1.4.11 non-text contrast).
 *
 * Values are CSS custom-property names rather than hex, so everything here is
 * on-palette by construction and `scripts/brand_lint.py` has nothing to catch.
 */
export interface CategoryTheme {
  /** Decorative only — the card's top edge. No contrast requirement. */
  accent: string;
  /** Must stay legible: icon stroke and category label. */
  ink: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

const FALLBACK: CategoryTheme = {
  accent: 'var(--davinci-stone)',
  ink: 'var(--davinci-grey)',
  icon: BriefcaseIcon,
};

const THEMES: Record<string, CategoryTheme> = {
  'AI & Automation': {
    accent: 'var(--davinci-blue)',
    ink: 'var(--davinci-blue-fill)',
    icon: CpuChipIcon,
  },
  'Cloud & Identity': {
    accent: 'var(--davinci-denim)',
    ink: 'var(--davinci-denim)',
    icon: CloudIcon,
  },
  'Custom Software Development': {
    accent: 'var(--davinci-midnight)',
    ink: 'var(--davinci-midnight)',
    icon: CodeBracketIcon,
  },
  'Managed IT & Infrastructure': {
    accent: 'var(--davinci-stone)',
    ink: 'var(--davinci-stone)',
    icon: ServerStackIcon,
  },
  'Systems Integration': {
    accent: 'var(--davinci-blue-fill)',
    ink: 'var(--davinci-blue-fill)',
    icon: ArrowsRightLeftIcon,
  },
  'Unified Communications': {
    // Beach is the palette's decorative light tint — fine as an edge, unreadable
    // as a stroke, so the icon borrows Denim.
    accent: 'var(--davinci-beach)',
    ink: 'var(--davinci-denim)',
    icon: PhoneIcon,
  },
  'Business Applications': {
    accent: 'var(--davinci-grey)',
    ink: 'var(--davinci-grey)',
    icon: BriefcaseIcon,
  },
};

/** Never throws on an unmapped category — a new one gets neutral styling. */
export const categoryTheme = (category: string): CategoryTheme =>
  THEMES[category] ?? FALLBACK;
